import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {PickerOptionRow} from '@/components/employee-picker'
import {rankMatches} from '@/lib/ranking'

/** La cumple la nómina y también la lista recortada, sin cédula, que el reporte de un accidente
 * muestra a cualquier miembro. */
export type PersonaSeleccionable = {
  id: string
  name: string
  last_name: string
  cedula?: string
  status?: string
  area?: string | null
}

/** Una lista sin `status` (la recortada) se toma como activa: el backend ya la filtró. */
export function EmployeeSingleSelect<T extends PersonaSeleccionable>({
  roster,
  value,
  onChange,
  exclude,
  testId,
}: {
  roster: T[]
  value: T | null
  onChange: (employee: T | null) => void
  exclude?: (employee: T) => boolean
  testId: string
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')
  const actives = useMemo(() => roster.filter((e) => (e.status ?? 'active') === 'active'), [roster])
  const matches = useMemo(() => rankMatches(actives, query, exclude), [actives, query, exclude])

  if (value) {
    return (
      <div className="flex items-center gap-2" data-testid={`${testId}-current`}>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground">
          <span className="min-w-0 truncate">
            {value.name} {value.last_name}
          </span>
        </span>
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={() => onChange(null)}
          className="text-xs font-medium text-primary hover:underline">
          {t('jerarquia.clear')}
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      <input
        type="text"
        value={query}
        data-testid={`${testId}-search`}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('jerarquia.searchPerson')}
        className="min-h-11 w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:border-ring focus:outline-none md:min-h-0"
      />
      {query.trim() && (
        <div className="overflow-hidden rounded-md border border-border shadow-sm">
          {matches.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t('jerarquia.noMatches')}</p>
          ) : (
            matches.map((person) => (
              <PickerOptionRow
                key={person.id}
                person={person}
                testId={`${testId}-option`}
                onPick={() => {
                  onChange(person)
                  setQuery('')
                }}
              />
            ))
          )}
        </div>
      )}
      {actives.length === 0 && <Tag tone="neutral">{t('jerarquia.noActivePeople')}</Tag>}
    </div>
  )
}
